// @flow

import { getAmktBootstrap } from '@atlassian/mpac-api-client';
import { getFeatureFlagsClient } from '@atlassian/mpac-feature-flags';
import { setSentryUserId } from '@atlassian/mpac-utils';
import AnalyticsWebClient, { originType } from '@atlassiansox/analytics-web-client';
import camelCase from 'lodash/camelCase';
import forEach from 'lodash/forEach';
import { parse } from 'query-string';

import bootstrapCommon from './bootstrap.common';

import type { ApplicationConfig } from '../../application-config/types/BootstrapConfig';

const queryPramsObj = parse(location.search);

const camelCaseObjectKeys = obj => {
  const newObj = {};
  forEach(obj, (value, key) => {
    newObj[camelCase(key)] = value;
  });

  return newObj;
};

// eslint-disable-next-line max-lines-per-function -- Fix me
export default async function buildBootstrap(config: ApplicationConfig) {
  const amktBootstrap = await getAmktBootstrap(config.mpacBaseUrl);
  const origin = navigator.userAgent.includes('Electron/') ? originType.DESKTOP : originType.WEB;
  const analyticsClient = new AnalyticsWebClient(
    {
      env: config.analytics.productAnalytics.env,
      product: 'marketplace',
      origin,
    },
    {
      useLegacyUrl:
        config.analytics.productAnalytics.env === 'local' ||
        config.analytics.productAnalytics.env === 'dev',
    }
  );

  const featureFlagsClient = await getFeatureFlagsClient({
    apiKey: config.featureFlags.apiKey,
    environment: config.featureFlags.environment,
    userId: amktBootstrap.ldUserKey,
    analyticsClient,
    productKey: 'marketplace',
    customAttributes: {
      source: typeof queryPramsObj.source === 'string' ? queryPramsObj.source : 'unknown',
    },
  });

  const featureFlags = camelCaseObjectKeys(featureFlagsClient.getAllFlags().toJS());

  setSentryUserId(amktBootstrap.ldUserKey);

  return {
    ...bootstrapCommon,
    analyticsClient,
    graphQLGatewayUrl: config.graphQLGatewayUrl,
    mpacBaseUrl: config.mpacBaseUrl,
    mpacCdnBaseUrl: config.mpacCdnBaseUrl,
    macBaseUrl: config.macBaseUrl,
    wacBaseUrl: config.wacBaseUrl,
    atlassianCommunityBaseUrl: config.atlassianCommunityBaseUrl,
    jiraServiceDeskEmbedBaseUrl: config.jiraServiceDeskEmbedBaseUrl,
    environment: config.environment,
    sentryDsn: config.sentry.dsn,
    analytics: config.analytics,
    metal: config.metal,
    enableIdentityHeartbeats: config.enableIdentityHeartbeats,
    appConfig: {
      ...bootstrapCommon.appConfig,
      generalLinks: {
        ...bootstrapCommon.appConfig.generalLinks,
        files: {
          href: `${config.mpacCdnBaseUrl}/files/{fileName}`,
          templated: true,
        },
        getMailingListLocale: {
          href: `${config.preferencesBaseUrl}/rest/forms/locale`,
          templated: true,
        },
        subscribeToMailingList: {
          href: `${config.mpacBaseUrl}/rest/internal/newsletters/subscriptions`,
          templated: true,
        },
        subscribeToMailingListFailed: {
          href: `${
            config.mpacBaseUrl
          }/rest/internal/users/subscription-failed{?emailId,mailingListId}`,
          templated: true,
        },
      },
      commonConfig: {
        algoliaConfig: {
          applicationId: config.algolia.applicationId,
          apiKey: config.algolia.apiKey,
        },
        contentfulConfig: {
          authToken: amktBootstrap.isAdmin
            ? '95c3b871aa1b5e5a103917933a44ff649850e1c94de1cfbb2cf0e9f8ba32704c'
            : '34f9a2bf6ab2d9c35b7f312bd5b559c69e4386c11d441c5660add1df7821bb31',
          spaceId: 'xi2je6di2mcr',
          previewMode: !!amktBootstrap.isAdmin,
        },
        googleAnalyticsId: config.analytics.googleAnalyticsId,
        environment: config.algolia.environment,
        features: {
          strideHipchatBanners: featureFlags.strideHipchatBanners,
        },
      },
      vendorDetails: {
        ...bootstrapCommon.appConfig.vendorDetails,
        links: {
          ...bootstrapCommon.appConfig.vendorDetails.links,
          vendorAddonsRest: {
            href: `${
              config.mpacBaseUrl
            }/rest/2/addons/vendor/{vendorId}{?application,applicationBuild,category*,cost,filter,forThisUser,hosting*,includeHidden,includePrivate,marketingLabel*,text,withVersion,storesPersonalData,offset,limit}`,
            templated: true,
          },
          vendorArchivedAddonsRest: {
            href: `${
              config.mpacBaseUrl
            }/rest/2/addons/archived/vendor/{vendorId}{?application,applicationBuild,category*,cost,filter,forThisUser,hosting*,includeHidden,includePrivate,marketingLabel*,text,withVersion,storesPersonalData,offset,limit}`,
            templated: true,
          },
          vendorDetailsRest: {
            href: `${config.mpacBaseUrl}/rest/2/vendors/{vendorId}`,
            templated: true,
          },
        },
      },
    },
    currentUserLink: {
      url: `${config.mpacBaseUrl}/rest/internal/users/current`,
    },
    featureFlags,
  };
}
